import type { Season } from '@/time/clock';

/**
 * Fish content.
 *
 * Real species, sorted by the water they actually live in. Availability filters
 * over water, season and hour rather than being listed per fishing spot, so a
 * new pond or a new coastline needs no new data — and "sea bass off the rocks
 * at dawn in autumn" becomes something a player learns and plans around, which
 * is where the pleasure in fishing lives.
 */

export const WATER_KINDS = ['pond', 'lake', 'river', 'ocean'] as const;
export type WaterKind = (typeof WATER_KINDS)[number];

export const WATER_NAMES: Readonly<Record<WaterKind, string>> = {
  pond: 'Pond',
  lake: 'Lake',
  river: 'River',
  ocean: 'Ocean',
};

export interface FishDefinition {
  // Deliberately `string`, not FishId: FishId is derived from this table, so
  // narrowing the field here makes the type circular and collapses the union
  // straight back to `string`.
  readonly id: string;
  readonly name: string;
  /** Bodies of water this species lives in. */
  readonly waters: readonly WaterKind[];
  readonly seasons: readonly Season[];
  /** Inclusive hour window. Wraps past midnight when `from` is greater. */
  readonly from: number;
  readonly to: number;
  /** Relative chance of being the one that bites, among everything available. */
  readonly weight: number;
  /** Base difficulty: fraction of the strike bar that counts as a hit. */
  readonly window: number;
  /** Typical length range in inches. */
  readonly minIn: number;
  readonly maxIn: number;
  /**
   * Rod strength needed to land it at all. A fish beyond the rod simply does
   * not bite: better to never hook a tuna on a bamboo rod than to hook it and
   * lose it every time.
   */
  readonly rodTier: number;
  /** Offerings this species will take. Nothing else will tempt it. */
  readonly baits: readonly string[];
  /** Weight of a maximum-length specimen, in pounds. */
  readonly maxLb: number;
  /** Price for an average specimen; actual value scales with length. */
  readonly sellPrice: number;
}

const ALL_SEASONS: readonly Season[] = ['spring', 'summer', 'autumn', 'winter'];

/**
 * Weight is roughly inverse to value: the common fish that keeps a beginner
 * fed bites constantly, the trophy almost never.
 */
const SPECIES = [
  // --- Pond -------------------------------------------------------------
  { id: 'bluegill', name: 'Bluegill', waters: ['pond', 'lake'], seasons: ALL_SEASONS, from: 6, to: 20, weight: 40, window: 0.42, minIn: 4, maxIn: 10, rodTier: 1, baits: ['worm', 'grub'], maxLb: 1.32, sellPrice: 20 },
  { id: 'crucianCarp', name: 'Crucian Carp', waters: ['pond'], seasons: ['spring', 'summer', 'autumn'], from: 5, to: 21, weight: 30, window: 0.36, minIn: 6, maxIn: 18, rodTier: 1, baits: ['worm', 'bread'], maxLb: 6.61, sellPrice: 35 },
  { id: 'pondLoach', name: 'Pond Loach', waters: ['pond'], seasons: ['spring', 'autumn'], from: 0, to: 23, weight: 22, window: 0.34, minIn: 3, maxIn: 8, rodTier: 1, baits: ['worm'], maxLb: 0.33, sellPrice: 30 },
  { id: 'tilapia', name: 'Tilapia', waters: ['pond'], seasons: ['summer'], from: 8, to: 18, weight: 20, window: 0.32, minIn: 8, maxIn: 16, rodTier: 2, baits: ['bread', 'grub'], maxLb: 5.51, sellPrice: 55 },
  { id: 'koi', name: 'Koi', waters: ['pond'], seasons: ['spring', 'summer'], from: 6, to: 18, weight: 7, window: 0.2, minIn: 12, maxIn: 35, rodTier: 3, baits: ['bread', 'spinner'], maxLb: 33, sellPrice: 220 },
  { id: 'goldenCarp', name: 'Golden Carp', waters: ['pond', 'lake'], seasons: ALL_SEASONS, from: 22, to: 4, weight: 3, window: 0.14, minIn: 16, maxIn: 39, rodTier: 4, baits: ['glowLure'], maxLb: 49, sellPrice: 420 },

  // --- Lake -------------------------------------------------------------
  { id: 'yellowPerch', name: 'Yellow Perch', waters: ['lake', 'river'], seasons: ['spring', 'autumn', 'winter'], from: 6, to: 19, weight: 34, window: 0.38, minIn: 5, maxIn: 12, rodTier: 1, baits: ['worm', 'grub'], maxLb: 1.54, sellPrice: 30 },
  { id: 'largemouthBass', name: 'Largemouth Bass', waters: ['lake'], seasons: ['spring', 'summer', 'autumn'], from: 5, to: 21, weight: 26, window: 0.3, minIn: 10, maxIn: 24, rodTier: 2, baits: ['spinner', 'grub'], maxLb: 13, sellPrice: 80 },
  { id: 'lakeTrout', name: 'Lake Trout', waters: ['lake'], seasons: ['autumn', 'winter'], from: 4, to: 12, weight: 20, window: 0.27, minIn: 12, maxIn: 31, rodTier: 2, baits: ['spinner', 'flyLure'], maxLb: 33, sellPrice: 110 },
  { id: 'walleye', name: 'Walleye', waters: ['lake', 'river'], seasons: ['autumn', 'winter'], from: 19, to: 5, weight: 16, window: 0.24, minIn: 12, maxIn: 30, rodTier: 3, baits: ['spinner', 'worm'], maxLb: 18, sellPrice: 130 },
  { id: 'northernPike', name: 'Northern Pike', waters: ['lake', 'river'], seasons: ['summer', 'autumn'], from: 6, to: 20, weight: 10, window: 0.2, minIn: 18, maxIn: 47, rodTier: 3, baits: ['spinner', 'spoonLure'], maxLb: 55, sellPrice: 200 },
  { id: 'muskellunge', name: 'Muskellunge', waters: ['lake'], seasons: ['autumn'], from: 5, to: 10, weight: 3, window: 0.13, minIn: 28, maxIn: 59, rodTier: 4, baits: ['spoonLure'], maxLb: 71, sellPrice: 480 },

  // --- River ------------------------------------------------------------
  { id: 'minnow', name: 'Minnow', waters: ['river', 'pond'], seasons: ALL_SEASONS, from: 0, to: 23, weight: 42, window: 0.44, minIn: 2, maxIn: 5, rodTier: 1, baits: ['worm', 'grub', 'bread'], maxLb: 0.04, sellPrice: 12 },
  { id: 'chub', name: 'Chub', waters: ['river'], seasons: ALL_SEASONS, from: 6, to: 20, weight: 32, window: 0.38, minIn: 6, maxIn: 18, rodTier: 1, baits: ['worm', 'bread'], maxLb: 5.51, sellPrice: 28 },
  { id: 'rainbowTrout', name: 'Rainbow Trout', waters: ['river'], seasons: ['spring', 'summer'], from: 6, to: 18, weight: 26, window: 0.31, minIn: 8, maxIn: 22, rodTier: 2, baits: ['flyLure', 'grub'], maxLb: 8.82, sellPrice: 75 },
  { id: 'brownTrout', name: 'Brown Trout', waters: ['river', 'lake'], seasons: ['spring', 'autumn'], from: 5, to: 11, weight: 20, window: 0.29, minIn: 9, maxIn: 24, rodTier: 2, baits: ['flyLure', 'worm'], maxLb: 11, sellPrice: 90 },
  { id: 'grayling', name: 'Grayling', waters: ['river'], seasons: ['winter'], from: 6, to: 18, weight: 22, window: 0.28, minIn: 8, maxIn: 18, rodTier: 2, baits: ['flyLure'], maxLb: 4.41, sellPrice: 95 },
  { id: 'smallmouthBass', name: 'Smallmouth Bass', waters: ['river'], seasons: ['summer', 'autumn'], from: 5, to: 20, weight: 18, window: 0.28, minIn: 9, maxIn: 22, rodTier: 2, baits: ['spinner', 'grub'], maxLb: 9.92, sellPrice: 85 },
  { id: 'catfish', name: 'Catfish', waters: ['river', 'pond'], seasons: ['spring', 'summer', 'autumn'], from: 18, to: 3, weight: 14, window: 0.22, minIn: 12, maxIn: 39, rodTier: 3, baits: ['worm', 'shrimp'], maxLb: 44, sellPrice: 150 },
  { id: 'salmon', name: 'Salmon', waters: ['river', 'ocean'], seasons: ['autumn'], from: 5, to: 19, weight: 12, window: 0.21, minIn: 16, maxIn: 35, rodTier: 3, baits: ['spoonLure', 'shrimp'], maxLb: 31, sellPrice: 180 },
  { id: 'sturgeon', name: 'Sturgeon', waters: ['river', 'lake'], seasons: ['summer', 'winter'], from: 6, to: 16, weight: 4, window: 0.13, minIn: 31, maxIn: 79, rodTier: 4, baits: ['shrimp', 'worm'], maxLb: 198, sellPrice: 520 },

  // --- Ocean ------------------------------------------------------------
  { id: 'sardine', name: 'Sardine', waters: ['ocean'], seasons: ALL_SEASONS, from: 6, to: 19, weight: 40, window: 0.44, minIn: 4, maxIn: 9, rodTier: 1, baits: ['shrimp', 'grub'], maxLb: 0.26, sellPrice: 18 },
  { id: 'anchovy', name: 'Anchovy', waters: ['ocean'], seasons: ['spring', 'autumn'], from: 5, to: 20, weight: 34, window: 0.42, minIn: 3, maxIn: 7, rodTier: 1, baits: ['shrimp'], maxLb: 0.11, sellPrice: 22 },
  { id: 'herring', name: 'Herring', waters: ['ocean'], seasons: ['winter', 'spring'], from: 0, to: 23, weight: 30, window: 0.38, minIn: 6, maxIn: 14, rodTier: 1, baits: ['shrimp', 'spinner'], maxLb: 1.54, sellPrice: 32 },
  { id: 'mackerel', name: 'Mackerel', waters: ['ocean'], seasons: ['summer', 'autumn'], from: 5, to: 18, weight: 26, window: 0.34, minIn: 10, maxIn: 20, rodTier: 2, baits: ['spinner', 'shrimp'], maxLb: 4.41, sellPrice: 48 },
  { id: 'flounder', name: 'Flounder', waters: ['ocean'], seasons: ['spring', 'summer'], from: 6, to: 20, weight: 22, window: 0.3, minIn: 10, maxIn: 24, rodTier: 2, baits: ['shrimp', 'worm'], maxLb: 11, sellPrice: 70 },
  { id: 'cod', name: 'Cod', waters: ['ocean'], seasons: ['autumn', 'winter'], from: 4, to: 16, weight: 22, window: 0.3, minIn: 14, maxIn: 35, rodTier: 2, baits: ['shrimp', 'spoonLure'], maxLb: 55, sellPrice: 85 },
  { id: 'seaBass', name: 'Sea Bass', waters: ['ocean'], seasons: ALL_SEASONS, from: 17, to: 6, weight: 18, window: 0.27, minIn: 12, maxIn: 31, rodTier: 3, baits: ['spoonLure', 'shrimp'], maxLb: 18, sellPrice: 110 },
  { id: 'redSnapper', name: 'Red Snapper', waters: ['ocean'], seasons: ['summer', 'autumn'], from: 6, to: 19, weight: 14, window: 0.24, minIn: 12, maxIn: 30, rodTier: 3, baits: ['shrimp', 'spinner'], maxLb: 22, sellPrice: 140 },
  { id: 'halibut', name: 'Halibut', waters: ['ocean'], seasons: ['spring', 'winter'], from: 5, to: 15, weight: 10, window: 0.2, minIn: 20, maxIn: 63, rodTier: 3, baits: ['spoonLure', 'shrimp'], maxLb: 220, sellPrice: 230 },
  { id: 'mahiMahi', name: 'Mahi-mahi', waters: ['ocean'], seasons: ['summer'], from: 8, to: 18, weight: 8, window: 0.18, minIn: 24, maxIn: 51, rodTier: 4, baits: ['spoonLure', 'flyLure'], maxLb: 66, sellPrice: 290 },
  { id: 'tuna', name: 'Tuna', waters: ['ocean'], seasons: ['summer', 'winter'], from: 4, to: 12, weight: 6, window: 0.15, minIn: 28, maxIn: 79, rodTier: 4, baits: ['spoonLure'], maxLb: 397, sellPrice: 400 },
  { id: 'swordfish', name: 'Swordfish', waters: ['ocean'], seasons: ['summer', 'autumn'], from: 22, to: 4, weight: 2, window: 0.11, minIn: 47, maxIn: 118, rodTier: 4, baits: ['spoonLure', 'glowLure'], maxLb: 992, sellPrice: 750 },

  // --- Anywhere, but only in the small hours ----------------------------
  { id: 'moonfish', name: 'Moonfish', waters: ['pond', 'lake', 'river', 'ocean'], seasons: ALL_SEASONS, from: 23, to: 3, weight: 3, window: 0.14, minIn: 10, maxIn: 28, rodTier: 4, baits: ['glowLure'], maxLb: 13, sellPrice: 320 },
] as const satisfies readonly FishDefinition[];

/**
 * Derived from the table rather than written out, so a species cannot exist in
 * one place and not the other. Keeping it a finite union also keeps item ids
 * exact — widening it to `string` makes every ITEMS lookup possibly-undefined.
 */
export type FishId = (typeof SPECIES)[number]['id'];

export const FISH = Object.fromEntries(
  SPECIES.map((fish) => [fish.id, fish]),
) as unknown as Readonly<Record<FishId, FishDefinition>>;

export function isFishId(value: unknown): value is FishId {
  return typeof value === 'string' && value in FISH;
}

/** Every species, for iteration. */
export const ALL_FISH: readonly FishDefinition[] = SPECIES;

/** Does this hour fall inside the window, including windows that cross midnight? */
export function isBiting(fish: FishDefinition, hour: number): boolean {
  return fish.from <= fish.to
    ? hour >= fish.from && hour <= fish.to
    : hour >= fish.from || hour <= fish.to;
}

export interface FishingConditions {
  readonly water: WaterKind;
  readonly season: Season;
  readonly hour: number;
  /** Strength of the rod in hand. */
  readonly rodTier: number;
  /** What is on the hook, or null when fishing bare. */
  readonly bait: string | null;
}

/**
 * What could bite, given where, when, and what you are offering.
 *
 * All four filters are the same kind of gate on purpose: a fish you cannot
 * reach is simply absent rather than present-but-unlandable. Hooking a tuna on
 * a bamboo rod and losing it every time teaches nothing; never hooking it, and
 * then hooking it the day you finish the steel rod, teaches everything.
 */
export function availableFish(conditions: FishingConditions): FishDefinition[] {
  const { water, season, hour, rodTier, bait } = conditions;
  return ALL_FISH.filter(
    (fish) =>
      fish.waters.includes(water) &&
      fish.seasons.includes(season) &&
      isBiting(fish, hour) &&
      fish.rodTier <= rodTier &&
      bait !== null &&
      fish.baits.includes(bait),
  );
}

/**
 * Pick what bites, weighted by rarity.
 *
 * `roll` is caller-supplied rather than an internal Math.random so a cast is
 * reproducible under test.
 */
export function pickFish(conditions: FishingConditions, roll: number): FishDefinition | null {
  const candidates = availableFish(conditions);
  if (candidates.length === 0) return null;

  const total = candidates.reduce((sum, fish) => sum + fish.weight, 0);
  let target = Math.min(Math.max(roll, 0), 0.999_999) * total;

  for (const fish of candidates) {
    target -= fish.weight;
    if (target < 0) return fish;
  }
  return candidates[candidates.length - 1]!;
}

/**
 * Roll a length.
 *
 * Two rolls averaged, so the distribution humps in the middle: most fish are
 * ordinary and both a runt and a monster are memorable. A single uniform roll
 * would make record-size catches as common as average ones, which drains the
 * meaning out of the record book.
 */
export function rollSize(fish: FishDefinition, rollA: number, rollB: number): number {
  const t = (clamp01(rollA) + clamp01(rollB)) / 2;
  return Math.round(fish.minIn + (fish.maxIn - fish.minIn) * t);
}

/** Where a length sits in the species range, 0..1. */
export function sizeFraction(fish: FishDefinition, inches: number): number {
  if (fish.maxIn === fish.minIn) return 1;
  return clamp01((inches - fish.minIn) / (fish.maxIn - fish.minIn));
}

/**
 * Value scales with length: half price for a runt, double for a record.
 * Linear, so the player can eyeball what a catch is worth.
 */
export function priceFor(fish: FishDefinition, inches: number): number {
  return Math.max(1, Math.round(fish.sellPrice * (0.5 + 1.5 * sizeFraction(fish, inches))));
}

/**
 * Bigger fish fight harder.
 *
 * The strike window narrows by up to 40% across the species range, so landing
 * a record is meaningfully harder than landing an average one of the same kind
 * — but never so tight that the fish that just bit is unlandable.
 */
export function windowFor(fish: FishDefinition, inches: number): number {
  return fish.window * (1 - 0.4 * sizeFraction(fish, inches));
}

/**
 * Weight from length, scaled cubically.
 *
 * Fish grow in three dimensions, so a specimen twice as long is roughly eight
 * times the weight — the standard length-weight relationship with the exponent
 * left at 3. One number per species (the weight at full length) is enough, and
 * it keeps the data honest: a 10ft swordfish reads near 1000lb, not a thousand
 * times a sardine.
 */
export function weightFor(fish: FishDefinition, inches: number): number {
  const ratio = inches / fish.maxIn;
  return Math.max(0.01, fish.maxLb * ratio * ratio * ratio);
}

/** Ounces below a pound, pounds above — how an angler would actually say it. */
export function formatWeight(lb: number): string {
  if (lb < 1) return `${Math.max(1, Math.round(lb * 16))} oz`;
  return `${lb.toFixed(lb < 10 ? 2 : 1)} lb`;
}

/** Feet and inches once a fish is long enough for feet to be the natural unit. */
export function formatLength(inches: number): string {
  if (inches < 12) return `${inches} in`;
  const feet = Math.floor(inches / 12);
  const rest = inches % 12;
  return rest === 0 ? `${feet} ft` : `${feet} ft ${rest} in`;
}

/** How a catch is described: "a 42cm Chub". */
export function describeCatch(fish: FishDefinition, inches: number): string {
  const fraction = sizeFraction(fish, inches);
  const adjective = fraction > 0.9 ? 'a monstrous ' : fraction < 0.1 ? 'a tiny ' : 'a ';
  return `${adjective}${formatLength(inches)} ${fish.name}`;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
